import dayjs, { Dayjs } from 'dayjs';

import { CRM_PATHS } from '../crmPaths';
import { ContactListState, DEFAULT_PAGE_SIZE, parseListState, toSearchParams } from '../contacts/contactList';
import { formatDay, personName } from '../contactDetail/contactDetail';
import { CallOutcome } from '../calls/logCall';

/**
 * The call queue (card 21) as plain data and pure functions, tested without
 * rendering: its settings in the URL, what each served person's reason and the
 * queue's summary say, and the caller's tally for the session.
 *
 * Backend reference: ilbp-backend docs/CARD_16_CALL_LOGGING.md §5 (the queue)
 * and CARD_21_LOG_A_CALL_AND_QUEUE.md (these screens and the summary).
 */

export type QueueMode = 'FIRST' | 'ONLY' | 'MINE';
export type QueueReason = 'FOLLOW_UP_DUE' | 'NEVER_CALLED' | 'NOT_CALLED_RECENTLY';

export const QUEUE_MODES: Array<{ value: QueueMode; param: string | null; label: string; hint: string }> = [
  {
    value: 'FIRST',
    param: null,
    label: 'Everyone, follow-ups first',
    hint: 'Follow-ups due, then people never called, then those called longest ago.',
  },
  { value: 'ONLY', param: 'followups', label: 'Only follow-ups due', hint: "Every admin's, earliest first." },
  { value: 'MINE', param: 'mine', label: 'Only my follow-ups due', hint: 'The ones you set, earliest first.' },
];

/** Leave out anyone called this recently. 24 hours is the API's default. */
export const REST_CHOICES: Array<{ hours: number; label: string }> = [
  { hours: 24, label: '24 hours' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '7 days' },
  { hours: 720, label: '30 days' },
  { hours: 0, label: 'Nobody: call anyone' },
];
export const DEFAULT_REST = 24;

export interface QueueSettings {
  /** Who: the contact list's own filters, in its own URL form. */
  list: ContactListState;
  mode: QueueMode;
  rest: number;
}

/** The queue's settings from its URL: the contact list's filter, plus `mode` and `rest`. Malformed values are dropped. */
export const parseQueueSettings = (params: URLSearchParams): QueueSettings => {
  const mode = QUEUE_MODES.find((m) => m.param && m.param === params.get('mode'))?.value ?? 'FIRST';
  const rest = Number(params.get('rest'));
  return {
    list: parseListState(params),
    mode,
    rest: params.has('rest') && REST_CHOICES.some((choice) => choice.hours === rest) ? rest : DEFAULT_REST,
  };
};

/** The URL form, leaving out every default. Sorting and paging are the list's, not the queue's. */
export const queueSearchParams = (settings: QueueSettings): URLSearchParams => {
  const params = toSearchParams({ ...settings.list, sort: null, page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const mode = QUEUE_MODES.find((m) => m.value === settings.mode)?.param;
  if (mode) params.set('mode', mode);
  if (settings.rest !== DEFAULT_REST) params.set('rest', String(settings.rest));
  return params;
};

/** The queue over a contact list's filter: "Call these people". */
export const queuePathFor = (list: ContactListState) => {
  const params = queueSearchParams({ list, mode: 'FIRST', rest: DEFAULT_REST });
  const query = params.toString();
  return query ? `${CRM_PATHS.callQueue}?${query}` : CRM_PATHS.callQueue;
};

/* ------------------------------------------------------------------ *
 * The summary (crmCallQueueSummary)
 * ------------------------------------------------------------------ */

export interface QueueSummary {
  inQueue: number;
  followUpsDue: number;
  neverCalled: number;
  notCalledRecently: number;
  resting: number;
  followUpsLater: number;
  beingCalled: number;
  cannotCall: number;
  restHours: number;
}

const n = (value: number) => value.toLocaleString('en-US');
export const people = (count: number) => `${n(count)} ${count === 1 ? 'person' : 'people'}`;

const restWords = (hours: number) => REST_CHOICES.find((choice) => choice.hours === hours)?.label ?? `${hours} hours`;

/** In the queue, by phase, in the order they are served. Rows at zero are kept: "0 follow-ups due" is news. */
export const queueRows = (summary: QueueSummary, mode: QueueMode): Array<{ key: string; label: string; count: number }> =>
  mode === 'FIRST'
    ? [
        { key: 'followUpsDue', label: 'Follow-ups due', count: summary.followUpsDue },
        { key: 'neverCalled', label: 'Never called', count: summary.neverCalled },
        { key: 'notCalledRecently', label: 'Called before, longest ago first', count: summary.notCalledRecently },
      ]
    : [{ key: 'followUpsDue', label: mode === 'MINE' ? 'Your follow-ups due' : 'Follow-ups due', count: summary.followUpsDue }];

/** Matched by the filter but not served now, and why. Only the reasons that apply. */
export const waitingRows = (summary: QueueSummary, mode: QueueMode): Array<{ key: string; label: string; count: number }> =>
  [
    ...(mode === 'FIRST'
      ? []
      : [{ key: 'others', label: 'Not follow-ups: left out in this order', count: summary.neverCalled + summary.notCalledRecently }]),
    {
      key: 'resting',
      label: summary.restHours ? `Called in the last ${restWords(summary.restHours)}` : 'Called recently',
      count: summary.resting,
    },
    { key: 'followUpsLater', label: 'A follow-up for a later date', count: summary.followUpsLater },
    { key: 'beingCalled', label: 'Being called by someone else now', count: summary.beingCalled },
    { key: 'cannotCall', label: 'Cannot be called: do not call, do not contact, or no phone', count: summary.cannotCall },
  ].filter((row) => row.count > 0);

/* ------------------------------------------------------------------ *
 * The person served (crmCallQueueNext)
 * ------------------------------------------------------------------ */

export interface QueuePhone {
  id: number;
  type: string | null;
  e164: string | null;
  rawDisplay: string | null;
  isPrimary: boolean;
  isBad: boolean;
}

/** A number to dial. `id` is null for a main number that is not a row: an app member's own, which only the app held. */
export type QueueNumber = Omit<QueuePhone, 'id'> & { id: number | null };

export interface QueueContact {
  id: number;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  doNotCall: boolean;
  doNotContact: boolean;
  canCall: boolean;
  countryCode: string | null;
  region: string | null;
  city: string | null;
  supportLevel: number | null;
  lifecycleStage: string | null;
  callCount: number;
  lastCalledAt: string | null;
  lastContactedAt: string | null;
  source: string;
  phones: Array<QueuePhone>;
  tags: Array<{ tagId: number; name: string; color: string | null }>;
  notes: Array<{ id: number; body: string; source: string; createdAt: string }>;
  owner: { id: number; firstName: string | null; lastName: string | null } | null;
  account: { id: number } | null;
}

export interface QueueItem {
  reason: QueueReason | null;
  cursor: string | null;
  claimExpiresAt: string | null;
  followUp: {
    id: number;
    occurredAt: string;
    followUpAt: string | null;
    outcome: string;
    notes: string | null;
    admin: { id: number; firstName: string | null; lastName: string | null } | null;
  } | null;
  contact: QueueContact | null;
}

/** "5 hours ago", "3 days ago", "4 months ago", "2 years ago": rounded down, as people say it. */
export const ago = (then: Dayjs, now: Dayjs) => {
  const units: Array<[dayjs.OpUnitType, string]> = [
    ['year', 'year'],
    ['month', 'month'],
    ['week', 'week'],
    ['day', 'day'],
    ['hour', 'hour'],
    ['minute', 'minute'],
  ];
  for (const [unit, word] of units) {
    const count = now.diff(then, unit as dayjs.QUnitType);
    if (count >= 1) return `${count} ${word}${count === 1 ? '' : 's'} ago`;
  }
  return 'just now';
};

export const REASON_WORDS: Record<QueueReason, string> = {
  FOLLOW_UP_DUE: 'Follow-up due',
  NEVER_CALLED: 'Never called',
  NOT_CALLED_RECENTLY: 'Not called recently',
};

/** Why this person now, in a sentence under their name. */
export const describeReason = (item: QueueItem, now: Dayjs = dayjs()): string => {
  const contact = item.contact;
  switch (item.reason) {
    case 'FOLLOW_UP_DUE': {
      const f = item.followUp;
      if (!f?.followUpAt) return 'A follow-up is due.';
      const who = personName(f.admin);
      return `Follow-up due ${dayjs(f.followUpAt).isSame(now, 'day') ? 'today' : `since ${formatDay(f.followUpAt)}`}, set${who ? ` by ${who}` : ''} on a call on ${formatDay(f.occurredAt)}.`;
    }
    case 'NEVER_CALLED':
      return 'Never called.';
    case 'NOT_CALLED_RECENTLY':
      return contact?.lastCalledAt
        ? `Last called ${ago(dayjs(contact.lastCalledAt), now)}, on ${formatDay(contact.lastCalledAt)}${contact.callCount > 1 ? ` (${n(contact.callCount)} calls)` : ''}.`
        : 'Not called recently.';
    default:
      return '';
  }
};

/**
 * Every number to offer the caller. `primaryPhone` is what decides they can be
 * called and is not always a row, so it is added when it is not, as on their
 * page (`phoneItems`). Without it an app member is served with nothing to dial.
 */
export const numbersOf = ({ phones, primaryPhone }: Pick<QueueContact, 'phones' | 'primaryPhone'>): Array<QueueNumber> =>
  primaryPhone && !phones.some((phone) => phone.e164 === primaryPhone)
    ? [{ id: null, type: null, e164: primaryPhone, rawDisplay: null, isPrimary: true, isBad: false }, ...phones]
    : [...phones];

/**
 * Phones in dialling order: the primary, then the rest; bad and undiallable ones
 * last. The sort is stable, so a main number that is not a row stays ahead of a
 * row still marked primary.
 */
export const dialOrder = (phones: ReadonlyArray<QueueNumber>): Array<QueueNumber> =>
  [...phones].sort((a, b) => rank(a) - rank(b));

const rank = (p: QueueNumber) => (p.isBad ? 4 : 0) + (p.e164 ? 0 : 2) + (p.isPrimary ? 0 : 1);

/** `tel:` link for a diallable number; none for one NationBuilder stored unreadably or marked bad. */
export const telHref = (phone: QueueNumber) => (phone.e164 && !phone.isBad ? `tel:${phone.e164}` : null);

/* ------------------------------------------------------------------ *
 * The hold on the person, and the session
 * ------------------------------------------------------------------ */

/** Seconds of the claim left: other callers are not served this person until then. */
export const holdLeft = (claimExpiresAt: string | null, now: number) =>
  claimExpiresAt ? Math.max(0, Math.round((Date.parse(claimExpiresAt) - now) / 1000)) : 0;

/** "14 min", "45 s". */
export const formatHold = (seconds: number) => (seconds >= 60 ? `${Math.ceil(seconds / 60)} min` : `${seconds} s`);

/** Under two minutes the hold is about to run out: say so. */
export const HOLD_WARNING_SEC = 120;

export interface Tally {
  logged: number;
  skipped: number;
  outcomes: Partial<Record<CallOutcome, number>>;
  /** When the first person was served. */
  startedAt: number | null;
}

export const NO_TALLY: Tally = { logged: 0, skipped: 0, outcomes: {}, startedAt: null };

export const countLogged = (tally: Tally, outcome: CallOutcome): Tally => ({
  ...tally,
  logged: tally.logged + 1,
  outcomes: { ...tally.outcomes, [outcome]: (tally.outcomes[outcome] ?? 0) + 1 },
});

export const countSkipped = (tally: Tally): Tally => ({ ...tally, skipped: tally.skipped + 1 });

/** "12 calls logged and 3 skipped in 25 min." */
export const describeTally = (tally: Tally, now: number) => {
  const calls = `${n(tally.logged)} call${tally.logged === 1 ? '' : 's'} logged`;
  const skipped = tally.skipped ? ` and ${n(tally.skipped)} skipped` : '';
  const minutes = tally.startedAt != null ? Math.max(1, Math.round((now - tally.startedAt) / 60_000)) : 0;
  return `${calls}${skipped}${minutes ? ` in ${minutes} min` : ''}.`;
};
